import { invoke } from '@tauri-apps/api/core';
import { invokeAsExtension, effectiveGrantedCapabilities } from './enforce';
import { invalidateSchemaCache, DB_CONNECT_COMMANDS, DEFAULT_DB_PORTS } from '../dbEngines';
import { getExtensionStorageValue, setExtensionStorageValue } from '../storageFiles';
import { useExtensionStore } from '../store';
import {
  isDbManager,
  listDbConnectionEnvelopes,
  descriptorFromVaultItem,
  readDbConnectionItem,
  isDbVaultLocked,
  deleteDbConnectionFromVault,
} from './dbVaultConnections';
import { isVaultLockedError, vaultLockedError, waitForVaultGate } from '../../services/vaultGate';

/**
 * `db:*` capabilities — back DB Manager's connect/query/disconnect calls, one
 * capability per engine so a manifest can be granted exactly the engines it
 * needs. `extensionId` is injected by the host from the RPC envelope, never
 * trusted from the guest's own payload, so Rust can scope/force-close
 * connections per extension regardless of what the guest claims.
 *
 * Methods are engine-namespaced (`db.postgres.query`) because hostBridge maps
 * one method to one owning capability. The un-namespaced `db.connect/query/
 * disconnect` aliases stay routed to MySQL for compatibility with bundles
 * written against the original single-engine API.
 */

// Host-side ledger of live (extensionId, connId) pairs, mirrored from every
// connect/disconnect that passes through this module (and the reconnect
// button in DbManagerResultsTab, via markDbConnectionLive). Restored DB tabs
// consult it at hydration time: a pair that isn't live gets the reconnect
// screen instead of querying a dead connection — and a pair that IS live
// (workspace switch, not a fresh launch) skips it.
const liveConnections = new Map(); // key -> { extensionId, connId, engine }
const connKey = (extensionId, connId) => `${extensionId}\u0000${connId}`;

export function isDbConnectionLive(extensionId, connId) {
  return liveConnections.has(connKey(extensionId, connId));
}

/** Live (extensionId, connId) pairs — for UI offering connection choices. */
export function listLiveDbConnections() {
  return [...liveConnections.values()];
}

/**
 * Enabled extensions holding any db:* grant — candidates for on-demand connects.
 *
 * Reads EFFECTIVE grants, not the persisted array: a compiled-in extension is
 * exempt from the capability gate and its persisted grant set is empty, so
 * asking the store directly would drop DB Manager out of the "New SQL query
 * tab" picker entirely (DbQueryTabPickerModal.js) while it was working fine
 * everywhere else.
 */
export function listDbCapableExtensionIds() {
  const { extensions, enabled } = useExtensionStore.getState();
  return Object.keys(extensions).filter(
    (id) => enabled[id] && effectiveGrantedCapabilities(id).some((cap) => cap.startsWith('db:'))
  );
}

/**
 * secureStorage key holding a LEGACY saved connection's password (the
 * pre-vault storage.json connections; the store behind it moved from the OS
 * keychain to hidden Vault items on 26.10.2026, same key). The migrated legacy
 * connection keeps its id 'default' AND its pre-multi-connection key
 * (`<engine>-password`) so remembered passwords survive the storage-format
 * migration; every connection created after multi-connection support uses a
 * per-connection key. Mirrors secureKeyFor() in the guest (db-manager
 * entry.js) — the two must stay in sync.
 */
export function dbPasswordKeychainKey(connId, engine) {
  return connId === 'default' ? `${engine}-password` : `conn-${connId}-password`;
}

/**
 * All of an extension's saved DB connections, in the multi-connection format:
 * `[{ id, name, engine, host, port, user, database, path, remember }]` under
 * sdk.storage key 'connections'. Profiles from before the multi-connection
 * refactor have a single object under 'connectionConfig' instead — surfaced
 * here as one entry with id 'default' (matching the guest's migration and
 * the connIds baked into restored tabs). Returns [] when nothing is saved.
 *
 * The legacy fallback fires ONLY when the 'connections' key is absent
 * (pre-migration profile read before the guest's first activation). Any
 * array — including [] — is authoritative: an empty array means the user
 * removed every connection, and falling back would resurrect them.
 */
export async function listSavedDbConnections(extensionId) {
  if (isDbManager(extensionId)) {
    const envelopes = await listDbConnectionEnvelopes();
    // null is "the vault could not answer" (locked), NOT "you have none" —
    // returning [] there would show an empty state to someone whose
    // connections are merely behind a lock.
    if (envelopes) {
      const fromVault = envelopes.map((item) => descriptorFromVaultItem(item)).filter(Boolean);
      if (fromVault.length > 0) return fromVault;
    }
    // Falls through to the legacy store, so connections saved before the vault
    // move keep working. Delete this fallback once they have been re-created.
  }
  const list = await getExtensionStorageValue(extensionId, 'connections');
  if (Array.isArray(list)) {
    return list.filter((c) => c && typeof c === 'object' && c.id);
  }
  const legacy = await getExtensionStorageValue(extensionId, 'connectionConfig');
  if (legacy && typeof legacy === 'object' && (legacy.host || legacy.path)) {
    return [{ ...legacy, id: 'default' }];
  }
  return [];
}

/**
 * One saved connection by id. Same absent-only legacy fallback as
 * listSavedDbConnections: an existing array that lacks connId means the
 * connection was removed — return null rather than reviving it from the
 * legacy key (restored 'default' tabs must not silently reconnect to a
 * server the user deleted).
 */
export async function readSavedDbConnection(extensionId, connId) {
  if (isDbManager(extensionId)) {
    const item = await readDbConnectionItem(connId).catch(() => null);
    if (item?.type === 'dbConnection') {
      return descriptorFromVaultItem(item, { data: item.data || {} });
    }
  }
  const list = await getExtensionStorageValue(extensionId, 'connections');
  if (Array.isArray(list)) {
    const found = list.find((c) => c && typeof c === 'object' && c.id === connId);
    return found || null;
  }
  // Legacy single-connection format only ever backed connId 'default'.
  if (connId === 'default') {
    const legacy = await getExtensionStorageValue(extensionId, 'connectionConfig');
    if (legacy && typeof legacy === 'object' && (legacy.host || legacy.path)) {
      return { ...legacy, id: 'default' };
    }
  }
  return null;
}

/**
 * Remove a saved connection wherever it actually lives.
 *
 * Deleting the vault item alone was not enough. A LEGACY connection has no
 * vault item, so the delete was a no-op that reported success: the row
 * vanished from the tree and then came back on the next reload — because
 * `listSavedDbConnections` still found it in storage.json — with its password
 * still in the OS credential store, which is exactly what the confirmation
 * dialog said would be removed.
 *
 * Every step is best-effort and independent: a connection may exist in one
 * store and not the other, and failing to clear the keychain must not stop the
 * storage entry going away.
 */
export async function deleteSavedDbConnection(extensionId, connId) {
  // Read BEFORE deleting: the keychain key is engine-scoped, and after the
  // vault item is gone there is nothing left to tell us which engine it was.
  const saved = await readSavedDbConnection(extensionId, connId).catch(() => null);

  if (isDbManager(extensionId)) {
    await deleteDbConnectionFromVault(connId).catch(() => {});
  }

  const list = await getExtensionStorageValue(extensionId, 'connections').catch(() => null);
  if (Array.isArray(list)) {
    const next = list.filter((c) => !(c && typeof c === 'object' && c.id === connId));
    // Only write when something actually changed — a no-op write still costs a
    // file round-trip, and rewriting the list is how the old code lost data.
    if (next.length !== list.length) {
      await setExtensionStorageValue(extensionId, 'connections', next).catch(() => {});
    }
  }

  // Only a legacy connection has a secureStorage password; a vault
  // connection's password went with its vault item above. Asking anyway
  // would reach for a secret that never existed.
  if (saved?.engine && saved.origin !== 'vault') {
    await invokeAsExtension('secure_storage_delete', {
      extensionId,
      key: dbPasswordKeychainKey(connId, saved.engine),
    }).catch(() => {});
  }

  markDbConnectionClosed(extensionId, connId);
}

/**
 * Establishes an extension's connection host-side from its saved config
 * (password from the vault when remembered) — the same sources the tab
 * reconnect button and the sidebar auto-connect use. Connections don't
 * survive the extension iframe's idle teardown (its disconnect-all backstop
 * guards against leaked pools), so host features bootstrap on demand through
 * this instead of assuming liveness. Returns the engine on success, null when
 * there's no usable saved config; connection failures reject.
 */
/**
 * Copy a saved descriptor's TLS settings onto a connect config, in place.
 *
 * Exists because THREE places build a connect config — this module, the
 * results tab's reconnect button, and the guest's own form — and TLS settings
 * that reach one but not another are the worst kind of bug: the connection
 * still works, just without the verification the user asked for. One function,
 * so they cannot drift.
 *
 * Absent fields stay absent. The Rust side reads absence as "today's
 * behaviour", so a descriptor saved before TLS existed keeps connecting exactly
 * as it did.
 */
export function applyTlsFields(config, saved) {
  if (!config || !saved) return config;
  for (const key of ['sslMode', 'sslRootCert', 'sslClientCert', 'sslClientKey']) {
    if (saved[key]) config[key] = saved[key];
  }
  return config;
}

/**
 * Thrown when a saved connection needs a password nobody has supplied.
 *
 * Callers catch this and prompt. It is a sentinel rather than a plain failure
 * because the alternative — connecting with an empty password — produces an
 * authentication error that looks like the SERVER rejecting you, when in fact
 * the app simply never asked.
 */
export const DB_PASSWORD_REQUIRED = 'db_password_required';

/**
 * Connect using a saved descriptor.
 *
 * `password` overrides whatever is stored, which is how a prompt feeds its
 * answer back in. Without it, a connection whose password was never remembered
 * (or whose stored copy has since gone) rejects with DB_PASSWORD_REQUIRED
 * rather than silently trying an empty one.
 *
 * `interactive` (default true — a click): a legacy password behind a locked
 * Vault opens the Vault gate and connects after the unlock. Background
 * redials (dbHealthMonitor) pass false and get a `vault_locked` rejection
 * instead, so nothing pops up on its own.
 */
export async function connectDbFromSavedConfig(extensionId, connId = 'default', { password, interactive = true } = {}) {
  const saved = await readSavedDbConnection(extensionId, connId);
  if (!saved) return null;
  const engine = DB_CONNECT_COMMANDS[saved.engine] ? saved.engine : null;
  if (!engine) return null;

  let config;
  if (engine === 'sqlite') {
    if (!saved.path) return null;
    config = { path: saved.path, createIfMissing: false };
  } else {
    let secret = password;
    const fromVault = saved.origin === 'vault' && engine !== 'sqlite';
    if (secret == null) {
      if (fromVault) {
        // Card 6.4: the vault's password is filled in by Rust
        // (`db_connect_vault`); here only whether one is stored. "No password
        // key" is what means "ask" — a stored empty one is a real answer.
        if (!saved.remember) throw new Error(DB_PASSWORD_REQUIRED);
      } else {
        // A legacy descriptor: secureStorage (the Vault), when remembered.
        const stored = saved.remember ? await readLegacyPassword(extensionId, connId, engine, interactive) : null;
        // A remembered password whose stored copy has vanished is the same
        // situation as never remembering one: we cannot supply it, so ask.
        // Note `stored` may legitimately be '' — a remembered empty password —
        // which is why this checks for null rather than falsiness.
        if (stored == null) throw new Error(DB_PASSWORD_REQUIRED);
        secret = stored;
      }
    }
    const parsedPort = parseInt(saved.port, 10);
    config = {
      host: saved.host || 'localhost',
      port: Number.isFinite(parsedPort) && parsedPort > 0 ? parsedPort : DEFAULT_DB_PORTS[engine],
      user: saved.user || '',
    };
    // A vault connection without a prompt answer sends NO password: Rust adds it.
    if (secret != null) config.password = secret;
    applyTlsFields(config, saved);
    if (saved.database) config.database = saved.database;
    // Postgres has no engine-level "no database" connect: sqlx targets a
    // database named after the user when none is given, which usually
    // doesn't exist. Default to the always-present 'postgres' maintenance DB.
    else if (engine === 'postgres') config.database = 'postgres';
    if (fromVault) {
      try {
        await invokeAsExtension('db_connect_vault', { extensionId, connId, engine, config });
      } catch (e) {
        if (String(e?.message || e).includes(DB_PASSWORD_REQUIRED)) throw new Error(DB_PASSWORD_REQUIRED);
        throw e;
      }
      markDbConnectionLive(extensionId, connId, engine);
      return engine;
    }
  }

  await invokeAsExtension(DB_CONNECT_COMMANDS[engine], { extensionId, connId, config });
  markDbConnectionLive(extensionId, connId, engine);
  return engine;
}

/**
 * A legacy connection's remembered password, null when none is stored. The
 * store is the Vault now: locked, a click opens the gate and reads again; a
 * background caller (or a closed gate) gets `vault_locked`, never a silent
 * "no password" that would turn into a needless password prompt.
 */
async function readLegacyPassword(extensionId, connId, engine, interactive) {
  const read = () =>
    invokeAsExtension('secure_storage_get', { extensionId, key: dbPasswordKeychainKey(connId, engine) });
  try {
    return await read();
  } catch (e) {
    if (!isVaultLockedError(e)) return null;
    if (interactive && (await waitForVaultGate('db-manager'))) {
      return read().catch(() => null);
    }
    throw vaultLockedError(e?.code || 'vault_locked');
  }
}

/**
 * Record a successful connect. Deliberately does NOT clear restored tabs'
 * `needsReconnect` flags — a session-restored tab keeps its Connect screen
 * until the user explicitly acts on it (its own Connect button, or opening
 * the table again from the sidebar tree), even when the sidebar extension
 * auto-connected in the background.
 *
 * `engine` is recorded for the health monitor's ping, which must learn it
 * WITHOUT reading the saved config — that read lands on vault_get_item,
 * which stamps vault auto-lock activity, and a 60-second heartbeat that
 * stamps activity is a vault that never locks. A caller that does not know
 * the engine keeps whatever was recorded before, so a re-mark cannot blind
 * the ping.
 */
export function markDbConnectionLive(extensionId, connId, engine) {
  const key = connKey(extensionId, connId);
  const prior = liveConnections.get(key);
  liveConnections.set(key, { extensionId, connId, engine: engine || prior?.engine || null });
  // A (re)connect may point at a different server/database — cached
  // table/column names for SQL completions must not survive it.
  invalidateSchemaCache(extensionId, connId);
}

/** The engine recorded at connect time, or null — see markDbConnectionLive. */
export function liveDbConnectionEngine(extensionId, connId) {
  return liveConnections.get(connKey(extensionId, connId))?.engine || null;
}

export function markDbConnectionClosed(extensionId, connId) {
  liveConnections.delete(connKey(extensionId, connId));
  invalidateSchemaCache(extensionId, connId);
}

/** All of an extension's connections — ExtensionHost's disconnect-all backstop. */
export function markExtensionDbConnectionsClosed(extensionId) {
  for (const [key, value] of [...liveConnections]) {
    if (value.extensionId === extensionId) liveConnections.delete(key);
  }
}

function makeConnect(engine) {
  return (extensionId, params) =>
    invoke(`db_${engine}_connect`, {
      extensionId,
      connId: params.connId,
      config: params.config,
    }).then((res) => {
      markDbConnectionLive(extensionId, params.connId, engine);
      return res;
    });
}

function makeDisconnect(engine) {
  return (extensionId, params) =>
    invoke(`db_${engine}_disconnect`, {
      extensionId,
      connId: params.connId,
    }).then((res) => {
      markDbConnectionClosed(extensionId, params.connId);
      return res;
    });
}

function engineCapability(engine, capabilityName, extraMethods = {}) {
  return {
    capability: capabilityName,
    methods: {
      [`db.${engine}.connect`]: makeConnect(engine),
      // The forwarded fields are listed one by one, never spread from the
      // guest's payload. That is deliberate and load-bearing: `multiStatement`
      // is the Rust side's opt-in to the multi-statement text protocol, and a
      // sandboxed extension must not be able to ask for it. Guests get the
      // prepared, single-statement path, full stop.
      [`db.${engine}.query`]: (extensionId, params) =>
        invoke(`db_${engine}_query`, {
          extensionId,
          connId: params.connId,
          sql: params.sql,
          params: params.params || [],
        }),
      [`db.${engine}.disconnect`]: makeDisconnect(engine),
      ...extraMethods,
    },
  };
}

export const dbCapability = engineCapability('mysql', 'db:mysql', {
  'db.connect': makeConnect('mysql'),
  'db.query': (extensionId, params) =>
    invoke('db_mysql_query', {
      extensionId,
      connId: params.connId,
      sql: params.sql,
      params: params.params || [],
    }),
  'db.disconnect': makeDisconnect('mysql'),
});

export const dbPostgresCapability = engineCapability('postgres', 'db:postgres');
export const dbSqliteCapability = engineCapability('sqlite', 'db:sqlite');

/** Tauri disconnect-all command per capability — ExtensionHost's teardown backstop. */
export const DB_DISCONNECT_ALL_COMMANDS = {
  'db:mysql': 'db_mysql_disconnect_all',
  'db:postgres': 'db_postgres_disconnect_all',
  'db:sqlite': 'db_sqlite_disconnect_all',
};

/**
 * True when DB Manager connections are behind a vault lock.
 *
 * The picker uses this to offer an unlock row instead of an empty state:
 * "you have no connections" and "your connections are locked" look identical
 * otherwise, and only one of them is the user's problem to solve.
 */
export async function dbVaultLockedForConnections() {
  return isDbVaultLocked();
}
